import { ORDER_TYPE } from '@exam-management/common/utils';
import { FormPercentageInput } from '@exam-management/components';
import { FilterOperator, QSetStatus, useGetPublishedQSetsQuery } from '@exam-management/graphql';
import { useParams } from '@exam-management/router';
import {
  type GridRowSelectionModel,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ErrorBox,
  Flex,
  Form,
  Text,
} from '@shared/components';
import { zodResolver } from '@shared/utils';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { BaseQSetList } from '../../components';
import { useQSetSearchState } from '../../hooks';
import {
  CheckQSetDraftToPublishDuplicationRateFormSchema,
  type CheckQSetDraftToPublishDuplicationRateFormValues,
} from '../../types';
import { transformPublishedQSetsToQSetRows } from '../../utils/transform-published-qsets-to-qset-rows';
import PublishedQSetListNoRowsOverlay from '../published-qset-list/published-qset-list-no-rows-overlay';

import { columns } from './columns';

const ALERT_CHILDREN_START_TEXT =
  'กรุณาเลือกชุดข้อสอบที่ต้องการตรวจสอบค่าความซ้ำอย่างน้อย 1 รายการ';

interface SelectQSetToCheckPublishedResultsModalProps {
  open: boolean;
  onClose: () => void;
  onFormSubmit: (formData: CheckQSetDraftToPublishDuplicationRateFormValues) => Promise<void>;
}

export default function SelectQSetToCheckPublishedResultsModal(
  props: Readonly<SelectQSetToCheckPublishedResultsModalProps>,
) {
  const { open: isOpen, onClose, onFormSubmit } = props;

  const formContext = useForm<CheckQSetDraftToPublishDuplicationRateFormValues>({
    defaultValues: {
      selectedPublishedQSetIds: [],
    },
    resolver: zodResolver(CheckQSetDraftToPublishDuplicationRateFormSchema),
  });

  const { formState } = formContext;

  const handleRowSelectionModelChange = (draftQSetIds: GridRowSelectionModel) => {
    formContext.setValue('selectedPublishedQSetIds', draftQSetIds.map(String), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onSubmit = async (data: CheckQSetDraftToPublishDuplicationRateFormValues) => {
    await onFormSubmit(data);
    handleClose();
  };

  const { courseId } = useParams('/exam-courses/:courseId/qsets');

  const [
    { page, pageSize, sortBy, sortOrder },
    { changePageNumber, changePageSize, changeSorting },
  ] = useQSetSearchState();

  const handleClose = () => {
    if (formState.isLoading) return;
    onClose();
  };

  const {
    data: publishedQSetsResult,
    loading: isSearchQSetsLoading,
    error: searchQSetsError,
  } = useGetPublishedQSetsQuery({
    variables: {
      searchQSetsInput: {
        paging: {
          pageSize: pageSize,
          currentPage: page,
        },
        order: {
          type: ORDER_TYPE[sortOrder],
          field: sortBy,
        },
        filter: {
          conditions: [
            {
              value: courseId,
              operator: FilterOperator.Equal,
              field: 'examCourseId',
            },
            {
              value: QSetStatus.Published,
              operator: FilterOperator.Equal,
              field: 'status',
            },
          ],
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const qSets = useMemo(
    () => transformPublishedQSetsToQSetRows(publishedQSetsResult?.excSearchQSets.qSets),
    [publishedQSetsResult?.excSearchQSets.qSets],
  );

  const totalQSets = publishedQSetsResult?.excSearchQSets.total ?? 0;

  const selectedPublishedQSetIdsLength = formContext.watch('selectedPublishedQSetIds')?.length ?? 0;

  return (
    <FormProvider {...formContext}>
      <Dialog
        onClose={handleClose}
        open={isOpen}
        maxWidth='lg'
        slotProps={{
          paper: {
            component: Form,
            onSubmit: formContext.handleSubmit(onSubmit),
            sx: { height: '100%' },
          },
        }}
      >
        <DialogTitle onClose={handleClose}>Check Published</DialogTitle>
        <Divider />
        <DialogContent
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(4),
          })}
        >
          <Flex
            sx={(theme) => ({
              maxWidth: '180px',
              flexDirection: 'column',
              gap: theme.spacing(1),
            })}
          >
            <Text variant='body2Semibold'>กำหนดค่าความซ้ำระหว่างชุด</Text>
            <FormPercentageInput name='duplicationRate' textAlign='left' />
          </Flex>
          <Alert
            severity='info'
            sx={(theme) => ({
              '.MuiAlert-icon': { display: selectedPublishedQSetIdsLength ? 'none' : '' },
              p: theme.spacing(3, 4),
            })}
          >
            {selectedPublishedQSetIdsLength
              ? `เลือก ${selectedPublishedQSetIdsLength} รายการ`
              : ALERT_CHILDREN_START_TEXT}
          </Alert>
          {searchQSetsError ? (
            <ErrorBox />
          ) : (
            <BaseQSetList
              columns={columns}
              autoHeight={false}
              rows={qSets}
              hideFooter={totalQSets === 0}
              loading={isSearchQSetsLoading}
              checkboxSelection
              onRowSelectionModelChange={handleRowSelectionModelChange}
              keepNonExistentRowsSelected
              slots={{
                noRowsOverlay: PublishedQSetListNoRowsOverlay,
              }}
              slotProps={{
                footer: {
                  total: totalQSets,
                  pagination: {
                    page: page,
                    count: totalQSets,
                    rowsPerPage: pageSize,
                    onPageChange: (_, page) => {
                      changePageNumber(page);
                    },
                    onRowsPerPageChange: (event) => {
                      changePageSize(parseInt(event.target.value));
                    },
                  },
                },
              }}
              onSortModelChange={(sorting) => {
                const sortModel = sorting[0];
                if (sortModel && sortModel.sort && sortModel.field) {
                  return changeSorting({
                    sortOrder: sortModel.sort,
                    sortBy: sortModel.field,
                  });
                }
                // Reset sorting to default
                return changeSorting({});
              }}
              sx={(theme) => ({
                borderRadius: theme.shape.rounded(2),
                border: `1px solid ${theme.palette.grey[300]}`,
                '.MuiDataGrid-columnHeaders': {
                  borderTop: 'none',
                  borderBottom: `1px solid ${theme.palette.grey[300]}`,
                },
                '.MuiDataGrid-cell:last-of-type': {
                  p: `${theme.spacing(3)} !important`,
                },
                '.MuiBox-root:last-of-type': {
                  borderTop: `1px solid ${theme.palette.grey[300]}`,
                },
                '.MuiDataGrid-row--lastVisible': {
                  borderBottom: 'none',
                },
              })}
            />
          )}
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            disabled={formState.isSubmitting}
            variant='outlined'
            color='secondary'
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            loading={formState.isSubmitting}
            disabled={!formContext.formState.isValid}
            type='submit'
          >
            Check Duplicates
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
